import ApiClient from '@/services/apiClient/ApiClient'
import type {
  TagWithLands,
  LandTagWithDetails,
  TagWithBuildings,
  BuildingTagWithDetails,
  Tag,
} from '@popety-io/types'

const apiClient = new ApiClient()

export const getTagsWithLandAssociations = async (): Promise<
  TagWithLands[]
> => {
  try {
    // Get all land-tag associations
    const tagsResponse = await apiClient.get('/tags/land/all')
    if (tagsResponse.errors) {
      throw new Error(tagsResponse.errors[0].message)
    }

    const LandTagWithDetailss: LandTagWithDetails[] = tagsResponse.data

    // Group by tag and collect land IDs - ONLY for primary tags
    const tagMap = new Map<string, TagWithLands>()

    LandTagWithDetailss.forEach((relation) => {
      // Only include primary tags for map coloring
      if (!relation.primary) return

      const tagId = relation.tag.id

      if (tagMap.has(tagId)) {
        // Add land ID to existing tag
        const existingTag = tagMap.get(tagId)!
        existingTag.land_ids.push(Number(relation.land_id))
      } else {
        // Create new tag entry
        tagMap.set(tagId, {
          ...relation.tag,
          user: relation.user,
          land_ids: [Number(relation.land_id)],
        })
      }
    })

    return Array.from(tagMap.values())
  } catch {
    throw new Error('Failed to fetch tags with land associations')
  }
}

// Get tags for a specific land
export const getTagsForLand = async (
  landId: string
): Promise<LandTagWithDetails[]> => {
  try {
    const response = await apiClient.get(`/tags/land/${landId}`)
    if (response.errors) {
      throw new Error(response.errors[0].message)
    }
    return response.data as LandTagWithDetails[]
  } catch (error: any) {
    console.error(`Failed to get tags for land ${landId}:`, error)
    throw new Error(error.message || 'Failed to fetch tags for land')
  }
}

// Add a new tag to a land
export const addTagToLand = async (
  landId: string,
  tagValue: string,
  color: string
): Promise<LandTagWithDetails> => {
  try {
    const response = await apiClient.post('/tags/land', {
      landId: parseInt(landId),
      tagValue: tagValue.trim(),
      color,
    })
    if (response.errors) {
      throw new Error(response.errors[0].message)
    }
    return response.data as LandTagWithDetails
  } catch (error: any) {
    console.error(`Failed to add tag to land ${landId}:`, error)
    throw new Error(error.message || 'Failed to add tag to land')
  }
}

// Remove a tag from a land (using land_tag_id)
export const removeTagFromLand = async (landTagId: string): Promise<void> => {
  try {
    const response = await apiClient.delete(`/tags/land/${landTagId}`)
    if (response.errors) {
      throw new Error(response.errors[0].message)
    }
  } catch (error: any) {
    console.error(`Failed to remove land tag ${landTagId}:`, error)
    throw new Error(error.message || 'Failed to remove tag from land')
  }
}

// Get all available tags
export const getAllTags = async (): Promise<Tag[]> => {
  try {
    const response = await apiClient.get('/tags/all')
    if (response.errors) {
      throw new Error(response.errors[0].message)
    }
    return response.data as Tag[]
  } catch (error: any) {
    console.error('Failed to get all tags:', error)
    throw new Error(error.message || 'Failed to fetch all tags')
  }
}

// Create a new tag
export const createTag = async (value: string, color: string): Promise<Tag> => {
  try {
    const response = await apiClient.post('/tags', {
      value: value.trim(),
      color,
    })
    if (response.errors) {
      throw new Error(response.errors[0].message)
    }
    return response.data as Tag
  } catch (error: any) {
    console.error('Failed to create tag:', error)
    throw new Error(error.message || 'Failed to create tag')
  }
}

// Create a land tag association
export const createLandTag = async (
  landId: string,
  tagId: string,
  primary: boolean = false
): Promise<LandTagWithDetails> => {
  try {
    const response = await apiClient.post('/tags/land', {
      land_id: landId,
      tag_id: tagId,
      primary,
    })
    if (response.errors) {
      throw new Error(response.errors[0].message)
    }
    return response.data as LandTagWithDetails
  } catch (error: any) {
    console.error(`Failed to create land tag for land ${landId}:`, error)
    throw new Error(error.message || 'Failed to create land tag')
  }
}

// ===== BUILDING TAG FUNCTIONS =====

// Get all tags with their building associations (only primary tags for coloring)
export const getTagsWithBuildingAssociations = async (): Promise<
  TagWithBuildings[]
> => {
  try {
    // Get all building-tag associations
    const tagsResponse = await apiClient.get('/tags/building/all')
    if (tagsResponse.errors) {
      throw new Error(tagsResponse.errors[0].message)
    }
    const BuildingTagWithDetailss: BuildingTagWithDetails[] = tagsResponse.data
    // Group by tag and collect building IDs - ONLY for primary tags
    const tagMap = new Map<string, TagWithBuildings>()
    BuildingTagWithDetailss.forEach((relation) => {
      // Only include primary tags for map coloring
      if (!relation.primary) return
      const tagId = relation.tag.id
      if (tagMap.has(tagId)) {
        // Add building ID to existing tag
        const existingTag = tagMap.get(tagId)!
        existingTag.building_ids.push(Number(relation.building_id))
      } else {
        // Create new tag entry
        tagMap.set(tagId, {
          ...relation.tag,
          user: relation.user,
          building_ids: [Number(relation.building_id)],
        })
      }
    })
    return Array.from(tagMap.values())
  } catch (error: any) {
    console.error('Failed to get tags with building associations:', error)
    throw new Error(
      error.message || 'Failed to get tags with building associations'
    )
  }
}

// Get tags for a specific building
export const getTagsForBuilding = async (
  buildingId: string
): Promise<BuildingTagWithDetails[]> => {
  try {
    const response = await apiClient.get(`/tags/building/${buildingId}`)
    if (response.errors) {
      throw new Error(response.errors[0].message)
    }
    return response.data as BuildingTagWithDetails[]
  } catch (error: any) {
    console.error(`Failed to get tags for building ${buildingId}:`, error)
    throw new Error(error.message || 'Failed to fetch tags for building')
  }
}

// Add a new tag to a building
export const addTagToBuilding = async (
  buildingId: string,
  tagId: string,
  primary: boolean
): Promise<BuildingTagWithDetails> => {
  try {
    const response = await apiClient.post('/tags/building', {
      building_id: parseInt(buildingId),
      tag_id: tagId,
      primary,
    })
    if (response.errors) {
      throw new Error(response.errors[0].message)
    }
    return response.data as BuildingTagWithDetails
  } catch (error: any) {
    console.error(`Failed to add tag to building ${buildingId}:`, error)
    throw new Error(error.message || 'Failed to add tag to building')
  }
}

// Remove a tag from a building
export const removeTagFromBuilding = async (tagId: string): Promise<void> => {
  try {
    const response = await apiClient.delete(`/tags/building/${tagId}`)
    if (response.errors) {
      throw new Error(response.errors[0].message)
    }
  } catch (error: any) {
    console.error(`Failed to remove tag ${tagId} from building`, error)
    throw new Error(error.message || 'Failed to remove tag from building')
  }
}
